import {UTCDate} from "@date-fns/utc";
import {differenceInSeconds} from "date-fns";
import keys from "lodash/keys";
import map from "lodash/map";
import pickBy from "lodash/pickBy";

import {
  PRIVATE_LOCATIONS_STATUS_MAP,
  PrivateLocation,
  PrivateLocationGlobalStatusCode,
  PrivateLocationStatus,
  PrivateLocationStatusCode,
  PrivateLocationStatusDetailItem,
  PrivateLocationStatusMapKey,
  StatusSeverity,
} from "@/lib/types/monitoring.ts";

export const getLastStatusReportSecsAgo = (status: PrivateLocationStatus) =>
  differenceInSeconds(new UTCDate(), status.sent_at ? new UTCDate(status.sent_at) : new UTCDate());

// Return list of error/warning messages corresponding to the private location status
// by matching PL-provided JSON detais with predefined severities dict and replacing
// PL-provided error messages with custom ones
const getMessagesWithSeverity = (status: PrivateLocationStatus, severity: StatusSeverity): string[] =>
  map(
    keys(
      pickBy(
        status.details,
        (value: PrivateLocationStatusDetailItem | null | undefined, key: string) =>
          value &&
          value.status === PrivateLocationStatusCode.ERROR &&
          PRIVATE_LOCATIONS_STATUS_MAP[key as PrivateLocationStatusMapKey] &&
          PRIVATE_LOCATIONS_STATUS_MAP[key as PrivateLocationStatusMapKey].severity === severity
      )
    ),
    (key) => PRIVATE_LOCATIONS_STATUS_MAP[key as PrivateLocationStatusMapKey].description
  );

export const getLocationStatus = (location: PrivateLocation) => {
  const lastStatusSecsAgo = getLastStatusReportSecsAgo(location.status);
  const errors = getMessagesWithSeverity(location.status, StatusSeverity.ERROR);
  const warnings = getMessagesWithSeverity(location.status, StatusSeverity.WARNING);

  let globalStatusCode;

  if (!location.status.status) {
    globalStatusCode = PrivateLocationGlobalStatusCode.READY_TO_DEPLOY;
  } else if (!location.status?.details || lastStatusSecsAgo > 30 * 60) {
    globalStatusCode = PrivateLocationGlobalStatusCode.NOT_REPORTING;
  } else if (errors.length > 0) {
    globalStatusCode = PrivateLocationGlobalStatusCode.ERROR;
  } else if (warnings.length > 0) {
    globalStatusCode = PrivateLocationGlobalStatusCode.WARNING;
  } else {
    globalStatusCode = PrivateLocationGlobalStatusCode.OPERATIONAL;
  }

  return {errors, warnings, globalStatusCode};
};
